import React, { useState } from 'react';
import omit from 'lodash/omit';
import styled, { css, useTheme } from 'styled-components';
import { Box, Icon, Pill, Text } from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { useProductDetails } from 'journey/productDetails/provider';
import { ProductDetailsActionTypes } from 'journey/productDetails/reducer/productDetails.reducer';
import { InfoLayerStatus } from 'journey/productDetails/typings';
import Clickable from 'shared/components/Clickable';
import { useQuery } from 'shared/hooks';
import ClickToCollectSection, {
  DeliveryOptionsInformationMiddleSection,
  InlineIcon,
} from './components/ClickToCollectSection';
import FindStoreLayer from './components/FindStoreLayer';
import { getFormattedDeliveryMessage } from './utils/getFormattedDeliveryMessage.utils';

const DeliveryOptionsInformationWrap = styled.div`
  ${({ theme: { spacing } }) => css`
    max-width: 360px;
    min-width: 315px;
    position: relative;
    margin-bottom: ${spacing.s5};
  `}
`;

const DeliveryOptionsSection = () => {
  const { dispatch, selectedDevice } = useProductDetails();
  const { palette } = useTheme();
  const [showFindStore, setShowFindStore] = useState(false);
  const {
    stock,
    isClickToCollectEnabled,
    deliveryMessage,
    extendedDelivery: isSelectedDeviceAvailableForPreorder,
  } = selectedDevice;
  const { replaceQuery, query } = useQuery();

  const isSelectedDeviceAvailableForStore = stock?.storeStatus === 'IN_STOCK';
  const isSelectedDeviceAvailableOnline = stock?.warehouseStatus === 'IN_STOCK';

  const shouldShowClickToCollect =
    isClickToCollectEnabled !== false || isSelectedDeviceAvailableForStore;

  const handleHideFindStore = () => {
    setShowFindStore(false);
    replaceQuery(omit(query, ['info']));
    trackCustomLink('close');
  };

  const handleClick = (contentKey: string, customLink: string) => {
    dispatch({
      type: ProductDetailsActionTypes.SetInfoLayer,
      infoLayer: {
        contentKey,
        status: InfoLayerStatus.RawHtml,
        isNested: false,
      },
    });
    trackCustomLink(customLink);
  };

  return (
    <>
      <FindStoreLayer
        showFindStore={showFindStore}
        handleHideFindStore={handleHideFindStore}
      />
      <LinkRegion regionId="deliveryInfo">
        <DeliveryOptionsInformationWrap>
          <Box flexDirection="row" justifyContent="space-between">
            <Box flexDirection="row">
              <InlineIcon name="cart_small" />
              <DeliveryOptionsInformationMiddleSection>
                <Text variant="bodySMedium" mb="s1">
                  Free delivery
                  <Clickable
                    aria-label="Free delivery available Information icon"
                    title="Free delivery available Information icon"
                    type="button"
                    onClick={() => {
                      handleClick(
                        'productDetails.freeDelivery',
                        'Free delivery Information icon',
                      );
                    }}
                  >
                    <Icon name="info_small" fill={palette.baseColors.lupin} />
                  </Clickable>
                </Text>
                <Text
                  variant="bodySMedium"
                  mb="s2"
                  color={palette.baseColors.greymouth}
                >
                  {getFormattedDeliveryMessage({
                    isSelectedDeviceAvailableOnline,
                    isSelectedDeviceAvailableForPreorder,
                    deliveryMessage,
                  })}
                </Text>
              </DeliveryOptionsInformationMiddleSection>
            </Box>
            <Box>
              {isSelectedDeviceAvailableOnline ? (
                <Pill
                  text={
                    isSelectedDeviceAvailableForPreorder
                      ? 'Pre-order'
                      : 'In stock'
                  }
                  color="sparkGreen"
                />
              ) : (
                <Pill text="Out of stock" color="moeraki" />
              )}
            </Box>
          </Box>
          {shouldShowClickToCollect ? (
            <ClickToCollectSection setShowFindStore={setShowFindStore} />
          ) : null}
        </DeliveryOptionsInformationWrap>
      </LinkRegion>
    </>
  );
};

export default DeliveryOptionsSection;
